import {
	forwardRef,
	ReactElement,
	Ref,
	useState,
	useImperativeHandle,
	createRef,
	Fragment
} from "react";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { createRoot, Root } from "react-dom/client";
import { ensureNotNil } from "client/Misc/utils";

type DialogFactory = (onClose: () => void) => ReactElement;

interface DialogRendererHandle {
	open(dialogFactory: DialogFactory): void;
};

interface DialogWithKey {
	dialog: ReactElement;
	key: Guid;
}

const DialogRenderer = forwardRef((_: {}, ref: Ref<DialogRendererHandle>) => {
	const [dialogs, setDialogs] = useState<DialogWithKey[]>([]);

	useImperativeHandle(ref, () => ({
		open(dialogFactory: DialogFactory) {
			const newDialogKey: Guid = newGuid();
			const newDialog: DialogWithKey = {
				key: newDialogKey,
				dialog: dialogFactory(() => {
					setDialogs((current) => current.filter(d => d.key !== newDialogKey));
				})
			};

			setDialogs((current) => [...current, newDialog]);
		}
	}));

	return (
		<>
			{dialogs.map(d => <Fragment key={d.key}>{d.dialog}</Fragment>)}
		</>
	);
});

DialogRenderer.displayName = "DialogRenderer";

const dialogRendererRef = createRef<DialogRendererHandle>();
const hostElement: HTMLElement | null = document.getElementById("react-dialogs-host");

if (hostElement) {
	const host: Root = createRoot(hostElement);
	host.render(<DialogRenderer ref={dialogRendererRef} />);
}

export function renderReactDialog(dialog: DialogFactory) {
	ensureNotNil(dialogRendererRef.current)
		.open(dialog);
}
